
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';

interface BlogContentProps {
  content: string;
  className?: string;
}

/**
 * TipTap always outputs content that starts with an HTML tag (<p>, <h2>, etc.)
 * Legacy posts use Markdown syntax (## heading, - list, **bold**)
 */
function isHtml(content: string): boolean {
  return /^\s*<[a-zA-Z]/.test(content.trim());
}

const PROSE_CLASSES = `
  prose prose-sm max-w-none
  prose-headings:font-semibold prose-headings:text-gray-800
  prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-2
  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-3
  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-3
  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-3
  prose-li:mb-1 prose-strong:font-semibold
  prose-a:text-blue-600 prose-a:underline
`.trim();

export function BlogContent({ content, className = "" }: BlogContentProps) {
  if (isHtml(content)) {
    // HTML from TipTap editor — sanitize and render directly
    const clean = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'blockquote', 'code', 'pre',
        'a', 'span', 'div', 'mark',
        'hr', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    });

    return (
      <div
        className={`${PROSE_CLASSES} ${className}`}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }

  // Legacy Markdown posts — keep original behavior
  const sanitized = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['#text'],
    KEEP_CONTENT: true,
  });

  return (
    <div className={`${PROSE_CLASSES} ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 className="text-2xl font-bold mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-semibold mt-4 mb-2">{children}</h2>,
          p:  ({ children }) => <p className="mb-2">{children}</p>,
          ul: ({ children }) => <ul className="list-disc pl-6 mb-2">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-6 mb-2">{children}</ol>,
          li: ({ children }) => <li className="mb-1">{children}</li>,
          a:  ({ href, children }) => {
            const safe = DOMPurify.sanitize(href || '', {
              ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
            });
            return (
              <a href={safe} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {children}
              </a>
            );
          },
        }}
      >
        {sanitized}
      </ReactMarkdown>
    </div>
  );
}
